// @flow
import type {TStateSwitcher} from 'modules/common/types';
import {useCallback, useState} from 'react';

/**
 * Возвращает значение и объект с функциями для переключения, уставновки в true и установки в false
 * @param defaultValue значение по умолчанию
 * @returns массив, где 1 элемент значение, а 2 объект с функциями для изменения значения
 */
export function useSwitchState(defaultValue: boolean): [boolean, TStateSwitcher] {
    const [isSomething, setIsSomething] = useState(defaultValue);

    const close = useCallback(() => {
        setIsSomething(false);
    }, [setIsSomething]);

    const open = useCallback(() => {
        setIsSomething(true);
    }, [setIsSomething]);

    const toggle = useCallback(() => {
        setIsSomething(!isSomething);
    }, [isSomething, setIsSomething]);

    return [isSomething, {close, open, toggle}];
}

/**
 * Позволяет узнать когда инпут в фокусе, надо повесить колбэки на инпут и в isFocused будет флаг инпута в фокусе
 * @param onBlur колбэк на выход из фокуса
 * @param onFocus колбэк на фокус
 * @returns {{handleBlur: *, handleFocus: *, isFocused: *}} состояние и колбэки
 */
export function useIsFocused(onBlur?: (SyntheticInputEvent<HTMLInputElement>) => void, onFocus?: (SyntheticInputEvent<HTMLInputElement>) => void) {
    const [isFocused, {close: unFocus, open: focus}] = useSwitchState(false);
    const handleBlur = useCallback(
        (e: SyntheticInputEvent<HTMLInputElement>) => {
            unFocus();
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur, unFocus]
    );
    const handleFocus = useCallback(
        (e: SyntheticInputEvent<HTMLInputElement>) => {
            focus();
            if (onFocus) {
                onFocus(e);
            }
        },
        [onFocus, focus]
    );

    return {handleBlur, handleFocus, isFocused};
}
